import { useEffect, useState } from 'react';

/**
 * Longest we hold the artwork back for.  A background PNG of several MB on a
 * slow link must not leave the page blank, so after this the images are shown
 * whatever state they are in and the browser paints them as they stream in.
 */
export const IMAGES_READY_TIMEOUT_MS = 2000;

/**
 * Resolve once `src` is fully fetched and decoded (or failed — a broken image
 * must not hold the others back).  `decode()` is preferred because it also
 * waits for the pixels to be ready to paint; browsers without it fall back
 * to the load event.
 */
const loadImage = (src) =>
    new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve();
        image.onerror = () => resolve();
        image.src = src;
        if (typeof image.decode === 'function') {
            image.decode().then(resolve, resolve);
        }
    });

/**
 * Fetch `sources` into the browser cache without waiting on them — for
 * artwork a later screen will need, so it is already there when that screen
 * mounts.  No-op without an `Image` constructor (SSR).
 *
 * @param {string[]} sources image URLs to warm
 * @returns {Promise<void>} settles once every request has finished or failed
 */
export function preloadImages(sources) {
    if (typeof Image !== 'function' || !Array.isArray(sources)) return Promise.resolve();
    return Promise.all(sources.filter(Boolean).map(loadImage)).then(() => undefined);
}

/**
 * `true` once every image in `sources` has been fetched and decoded, so a
 * backdrop and the illustration on top of it can be revealed in the same
 * frame instead of popping in one after the other as each download finishes
 * (login, waiting list and onboarding all layer a large gradient PNG under
 * an illustration).  Falls back to `true` after `IMAGES_READY_TIMEOUT_MS`
 * so a slow or failed download never blanks the page, and starts `true`
 * when there is nothing to wait for or no `Image` constructor (SSR).
 *
 * The requests warm the browser cache, so the `<img>` elements that render
 * the same URLs afterwards paint immediately.  Pass a stable array (module
 * constant or memoised) — the sources are compared by value on every render.
 *
 * @param {string[]} sources image URLs to wait for
 * @returns {boolean}
 */
export function useImagesReady(sources) {
    const list = Array.isArray(sources) ? sources.filter(Boolean) : [];
    const key = list.join('\n');
    const canLoad = typeof Image === 'function';
    const [readyKey, setReadyKey] = useState(() => (list.length && canLoad ? null : key));

    useEffect(() => {
        if (!list.length || !canLoad) return undefined;
        let active = true;
        const settle = () => {
            if (active) setReadyKey(key);
        };
        const timer = setTimeout(settle, IMAGES_READY_TIMEOUT_MS);
        Promise.all(list.map(loadImage)).then(settle);
        return () => {
            active = false;
            clearTimeout(timer);
        };
        // `key` stands in for `list` so a fresh-but-equal array does not refetch.
    }, [key, canLoad]);

    return readyKey === key;
}

export default useImagesReady;
